import { create } from "zustand";
import {
  getCredits,
  getSubscriptions,
  getLoginState,
  getPointsActivityPublicKey,
  pointsActivityCheckIn,
  getRemainNums,
} from "@/api";
import { APICode, SCENE_ID, PROJECT_TYPE } from "@/constants";
import {
  loadPaymentScript,
  loadLoginScript,
  encryptWithKey,
  autoShowOpenAppConfirm,
} from "@/utils";
import { toast } from "react-toastify";
import i18n from "@/locales";
import useCommonStore, { FeatureType, GeneratVideoType } from "./useCommonStore";
import EventHub from "@/utils/event";
import { GlobalEvent } from "@/constants/event";
import { t } from "i18next";

type TLoginOptions = {
  options?: {
    position?: string;
  };
  otherParam?: {
    scenesId?: string;
  },
  callback?: (userInfo: any) => void;
  errCallback?: () => void;
}
type TPaymentOptions = {
  options?: {
    position?: string;
    wantSVip?: boolean;
    payType?: string; // credit_sub, other, fotor_video_faceswap
  };
  otherParam?: {
    scenesId?: string;
    promotionId?: string;
  },
  callback?: () => void;
}

type States = {
  credits: number | undefined;
  userInfo: any;
  isVip: boolean | undefined;
  isSvip: boolean | undefined;
  publicKey: string;
  /** 剩余次数 */
  remainNums: number;
  mutiRemainNums: number;
  videoRemainNums: number;
};

type Getters = {
  getters: {
    isLogin: boolean | undefined;
    creditsVisible: boolean;
    currentRemainNums: number;
  };
};

type Mutations = {
  setCredits: (value: number) => void;
  setUserInfo: (value: any) => void;
};

type Actions = {
  reset: () => void;
  fetchCredits: () => Promise<number>;
  fetchUserInfo: () => Promise<any>;
  fetchVipStatus: () => Promise<void>;
  showPayment: (options: TPaymentOptions) => Promise<void>;
  showLogin: (options: TLoginOptions) => Promise<void>;
  receiveCredits: () => Promise<void>;
  fetchSingleRemainNums: () => Promise<void>;
  fetchMutiRemainNums: () => Promise<void>;
  fetchVideoRemainNums: () => Promise<void>;
  refreshFreeCount: () => Promise<void>;
  initUserInfo: (userInfo?: any) => Promise<any>;
  fetchAllRemainNums: () => Promise<any>;
  checkCanGenerate: () => Promise<boolean>;
  readyEnvironment: () => void;
};

const initialState: States = {
  credits: undefined,
  userInfo: null,
  isVip: undefined,
  isSvip: undefined,
  publicKey: "",
  remainNums: 0,
  mutiRemainNums: 0,
  videoRemainNums: 0,
};

const useUserStore = create<States & Getters & Mutations & Actions>(
  (set, get) => ({
    // states
    ...initialState,

    // getters
    getters: {
      get isLogin() {
        const { userInfo } = get();
        if (!userInfo) {
          return false
        }
        return userInfo.id ? true : false;
      },
      get creditsVisible() {
        const { credits, getters } = get();
        return !!(getters.isLogin && typeof credits === "number");
      },
      get currentRemainNums() {
        const { remainNums, mutiRemainNums, videoRemainNums } = get();
        const { featureType } = useCommonStore.getState();
        if (featureType === FeatureType.Single) {
          return remainNums;
        } else if (featureType === FeatureType.Multi) {
          return mutiRemainNums;
        } else if (featureType === FeatureType.Video) {
          return videoRemainNums;
        }
        return 0
      },
    },

    // mutations
    setCredits: (value) => set({ credits: value }),
    setUserInfo: (value) => set({ userInfo: value }),

    // actions
    reset: () => set(initialState),
    readyEnvironment: () => {
      loadLoginScript().then((isFirst) => {
        if (isFirst) {
          // 注册google一键登陆回调
          window.login.loginSuccess((res: any) => {
            get().initUserInfo(res.userInfo.data)
          });
        }
        // 首次初始化用户信息
        get().initUserInfo();
      })
    },
    initUserInfo: async (userInfo?: any) => {
      if (!userInfo) {
        await get().fetchUserInfo();
      } else {
        set({ userInfo: userInfo, remainNums: 0, mutiRemainNums: 0, videoRemainNums: 0 });
      }
      const isLogin = get().getters.isLogin;

      if (!isLogin) {
        // 获取剩余次数
        return await get().fetchAllRemainNums();
      } else {
        return Promise.allSettled([
          // 登录之后领取次数
          get().receiveCredits(),
          // 获取订阅信息
          get().fetchVipStatus(),
          // // 获取积分
          get().fetchCredits(),
          // 只获取视频剩余次数，登录之后只有视频预览用次数
          get().fetchVideoRemainNums(),
        ])
      }
    },
    fetchCredits: async () => {
      const { code, data } = await getCredits();
      if (code === APICode.Success) {
        const { subScore } = data;
        set({ credits: subScore });
        // 更新头部中的积分显示
        EventHub.getInstance().emit(GlobalEvent.REFRESH_CREDITS);
        return subScore;
      }
    },
    fetchUserInfo: () => {
      return new Promise<void>((resolve, reject) => {
        loadLoginScript().then(() => {
          window.login.FTGetLoginState().then((res: any) => {
            if (res && res.code === "000") {
              set({ userInfo: res.data, remainNums: 0, mutiRemainNums: 0, videoRemainNums: 0 });
              resolve(res.data);
            } else {
              resolve();
            }
          }).catch(reject);
        })
        // getLoginState()
        //   .then((res: any) => {
        //     if (res && res.code === "000") {
        //       set({ userInfo: res.data, remainNums: 0, mutiRemainNums: 0, videoRemainNums: 0 });
        //       resolve(res.data);
        //     } else {
        //       resolve();
        //     }
        //   })
        //   .catch(reject);
      });
    },
    fetchVipStatus() {
      return new Promise((resolve, reject) => {
        const { userInfo } = get();
        getSubscriptions()
          .then((res: any) => {
            if (res && res.code === "000") {
              let sub = res.data || [];
              if (
                sub.find(
                  (m: any) => m.productName === "fotor" && !m.expiration
                ) ||
                userInfo?.superFotor
              ) {
                set({ isVip: true });
              } else {
                set({ isVip: false });
              }
              if (
                sub.find((m: any) => m.productName === "pxbee" && !m.expiration)
              ) {
                // 超级vip
                set({ isVip: true, isSvip: true });
              } else {
                set({ isSvip: false });
              }
            }
            resolve();
          })
          .catch(reject);
      });
    },
    async showPayment({ options, otherParam = {}, callback }: TPaymentOptions) {
      const { featureType } = useCommonStore.getState();
      const featureName = featureType + "_swap";
      const position = options?.position || "";
      // 0 是默认选中vip, 1 是选中svip
      const wantVip = (options?.wantSVip || false) ? 1 : 0;
      // 展示位，用来控制展示哪些支付套餐 和 默认选中。优先级比wantVip高
      let payType = featureType === FeatureType.Video ? "fotor_video_faceswap" : "other";
      
      if (get().isVip || get().isSvip) {
        payType = "credit_sub";
      }

      return new Promise((resolve, reject) => {
        const { fetchVipStatus, fetchCredits, refreshFreeCount, isVip, isSvip } = get();
        loadPaymentScript()
          .then(() => {
            window["Payment"].showPayment(
              async (data: any) => {
                let { type, code } = data;
                if (code === "006" || code === "007" || type === "success") {
                  await Promise.allSettled([
                    fetchVipStatus(),
                    fetchCredits(),
                    refreshFreeCount(),
                  ]);
                  resolve();
                  callback?.();
                } else if (type === "fail") {
                  reject();
                } else {
                  reject();
                }
              },
              wantVip,
              payType,
              "",
              "",
              {
                position,
                project_type: PROJECT_TYPE,
                features_name: featureName,
              },
              otherParam
            );
          })
          .catch(reject);
      });
    },
    async showLogin({
      options,
      otherParam = {},
      callback,
      errCallback,
    }: TLoginOptions) {
      const features_name = useCommonStore.getState().featureType + "_swap";
      return new Promise<any>((resolve, reject) => {
        loadLoginScript()
          .then(() => {
            let position = (options && options.position) || "";
            window.login.showLogin(
              "signUp", // 默认是注册，且新版登录和注册是一个界面
              async (res: any) => {
                let { code, userInfo } = res;

                if (code === 4) {
                  // autoShowOpenAppConfirm();
                  errCallback?.();
                  reject();
                } else if (code === 0 || code === 1 || code === 2) {
                  set({ userInfo: userInfo.data });
                  callback?.(userInfo.data);
                  resolve(userInfo.data);
                } else {
                  errCallback?.();
                  reject();
                }
              },
              "",
              {
                position,
                project_type: PROJECT_TYPE,
                features_name: features_name,
              },
              true,
              {
                scenesId: SCENE_ID,
                ...otherParam,
              }
            );
          })
          .catch(reject);
      });
    },
    checkCanGenerate: async () => {
      const { isLogin } = get().getters;
      const { featureType } = useCommonStore.getState();
      try {
        // 未登录
        if (!isLogin) {
          const isUseRemains = useCommonStore.getState().getters.isUseRemains;
          // 先判断是否用次数，在判读次数是否足够
          if (!isUseRemains || get().getters.currentRemainNums <= 0) {
            // 唤起注册
            const userInfo = await get().showLogin({
              options: {
                position: "swap_generate",
              },
              otherParam: {
                scenesId: SCENE_ID,
              }
            });
            // 获取次数和积分等信息
            await get().initUserInfo(userInfo);
            return await get().checkCanGenerate();
          }
        } else {
          // TODO: 如果是vip或者svip，会排队给次数

          // 登录之后，还是用次数的情况
          if (useCommonStore.getState().getters.isUseRemains) {
            if (get().getters.currentRemainNums <= 0) {
              // 还是没有次数，且不是vip或者svip，升级
              if (!get().isSvip && !get().isVip) {
                await get().showPayment({
                  options: {
                    position: "swap_generate",
                  },
                  otherParam: {
                    scenesId: "032",
                  },
                });
                // 升级完都会给次数，所以不用处理啥
              }
              // 已经是vip或者svip了，提交等结果，交给后端判读，因为10分钟会给一次
              // TODO: 限流弹窗
            }
          } else {
            // 登录之后其它情况，用积分
            const credits = get().credits || 0;
            const creditConsume = useCommonStore.getState().getters.creditConsume;
            // 积分不足，升级
            if (credits < creditConsume) {
              await get().showPayment({
                options: {
                  position: "swap_generate",
                },
                otherParam: {
                  scenesId: "032",
                },
              });
            }
          }
        }
        // 返回true，代表可以生成
        return true;
      } catch (err) {
        console.warn(err);
        return false;
      }
    },
    receiveCredits: async () => {
      try {
        const {
          publicKey,
          userInfo,
          getters: { isLogin },
        } = get();

        if (!isLogin) return;

        if (!publicKey) {
          const { code, data } = await getPointsActivityPublicKey();
          if (code === APICode.Success) {
            set({ publicKey: data });
          }
        }

        const signData = {
          deviceId: userInfo.fotorToken,
          time: new Date().getTime(),
          uid: userInfo.fotorToken,
          type: "fotor_first_visit_faceswap",
        } as any;

        const sign = await encryptWithKey(
          JSON.stringify(signData),
          get().publicKey
        );

        const { code, data } = await pointsActivityCheckIn(sign);
        if (code === APICode.Success && data) {
          const { rewards } = data;
          if (rewards && rewards.length > 0 && rewards[0].type === "credits") {
            const [firstReward] = rewards;
            const { freeQuantity } = firstReward;
            toast(
              i18n.t("received_credits", {
                credits: freeQuantity || 2,
              })
            );
            get().fetchCredits();
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    refreshFreeCount: async () => {
      const featureType = useCommonStore.getState().featureType;
      if (featureType === FeatureType.Single) {
        await get().fetchSingleRemainNums();
      } else if (featureType === FeatureType.Multi) {
        await get().fetchMutiRemainNums();
      } else if (featureType === FeatureType.Video) {
        await get().fetchVideoRemainNums();
      }
    },
    fetchAllRemainNums: async () => {
      return Promise.allSettled([
        get().fetchSingleRemainNums(),
        get().fetchMutiRemainNums(),
        get().fetchVideoRemainNums(),
      ]);
    },
    fetchSingleRemainNums: async () => {
      try {
        const { code, data } = await getRemainNums("aiFaceSwap");
        if (code === APICode.Success) {
          set({ remainNums: data.remainNums });
        }
      } catch (err) {
        console.error(err);
      }
    },
    fetchMutiRemainNums: async () => {
      try {
        const { code, data } = await getRemainNums("aiMultiFaceSwap");
        if (code === APICode.Success) {
          set({ mutiRemainNums: data.remainNums });
        }
      } catch (err) {
        console.error(err);
      }
    },
    fetchVideoRemainNums: async () => {
      try {
        const { code, data } = await getRemainNums("aiFaceSwapVideo");
        if (code === APICode.Success) {
          set({ videoRemainNums: data.remainNums });
        }
      } catch (err) {
        console.error(err);
      }
    },
  })
);

export default useUserStore;
